import {
  captureEvent,
  extractErrorProperties,
} from "context/posthog/captureEvent";
import { POSTHOG_EVENTS, POSTHOG_FLOWS } from "context/posthog/events";
import { useSomethingWentWrongToast } from "hooks/useSomethingWentWrongToast";
import { isForbiddenError } from "modules/common/utils/isForbiddenError";

import { useTranslatedToasts } from "../../../hooks/toasts/useTranslatedToasts";
import { useRejectRefundMutation } from "../api/Refunds";
import { REFUNDS_TOASTS } from "../constants/Toasts";

import type { UUID } from "../../../utils";

export function useRejectRefund() {
  const [rejectRefund, { error, isError, isLoading }] =
    useRejectRefundMutation();
  const { showRejectSuccessToast, showForbiddenToast } = useTranslatedToasts(
    REFUNDS_TOASTS,
    "refunds"
  );

  // A 403 means the backend does not allow this user to reject the refund, so
  // it gets its own message instead of the generic failure one.
  const hasRejectPermission = !isForbiddenError(error);

  useSomethingWentWrongToast(
    isError && hasRejectPermission,
    undefined,
    undefined,
    error
  );

  const handleRejectRefund = async (refundId?: UUID) => {
    if (!refundId) {
      return;
    }

    try {
      await rejectRefund({ id: refundId }).unwrap();

      captureEvent(POSTHOG_EVENTS.rejectRefundSucceeded, {
        flow: POSTHOG_FLOWS.rejectRefund,
        entityId: refundId,
      });

      showRejectSuccessToast();
    } catch (rejectError) {
      // Not rethrown - it would end up as an unhandled rejection in the click
      // handler and be reported as a raw `{ status, data }` object.
      captureEvent(POSTHOG_EVENTS.rejectRefundFailed, {
        flow: POSTHOG_FLOWS.rejectRefund,
        entityId: refundId,
        ...extractErrorProperties(rejectError),
      });

      if (isForbiddenError(rejectError)) {
        showForbiddenToast();
      }
    }
  };

  return { rejectRefund: handleRejectRefund, isRejectRefundLoading: isLoading };
}
